import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ulid } from 'ulid';

// 공통 데이터 구조 인터페이스들
export interface TagItem {
  id: string;
  key: string;
  value: string;
}

export interface TaskNode {
  id: string;
  type: string;
  order: number;
  duration: { value: number; unit: 'hour' | 'day' };
}

export interface LanguageSettingsData {
  sourceLanguage: string;
  targetLanguages: string[];
  tags: string[];
  memo: string;
}

export interface WorkflowData {
  taskNodes: TaskNode[];
  connections: any[];
}

export interface WorkerAssignmentData {
  assignmentMatrix: any[];
  availableWorkers: any[];
  availableGroups: any[];
}

export interface FinalConfirmationData {
  jobs: any[];
  expandedJobs: any[];
  selectedLanguages: string[];
}

// 프리셋 Configuration 데이터 구조
export interface PresetConfigData {
  selectedPresetId: string;
  languageSettingsComponent: LanguageSettingsData;
  workflowComponent: WorkflowData;
  workerAssignmentComponent: WorkerAssignmentData;
  finalConfirmationComponent: FinalConfirmationData;
}

// 작업 프리셋 데이터 모델
export interface WorkPreset {
  id: string;
  name: string;
  description: string;
  tags: string[];
  category: string;
  // Configuration과 완전 동일한 형태의 데이터
  configData: PresetConfigData;
  // 사용자 상호작용 데이터
  userInteraction?: {
    isFavorite: boolean;
    lastUsedAt?: Date;
    usageCount: number;
    rating?: number;
  };
  // 메타데이터
  metadata?: {
    statistics: {
      totalUsages: number;
      successRate: number;
      averageCompletionTime: number;
      averageCost: number;
    };
    performance: {
      estimatedDuration: { value: number; unit: 'hour' | 'day' };
      estimatedCost: number;
      complexity: 'low' | 'medium' | 'high';
    };
    requirements: {
      supportedLanguages: string[];
      requiredSkills: string[];
    };
  };
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

interface WorkPresetStore {
  // 상태
  presets: WorkPreset[];
  isLoading: boolean;
  searchQuery: string;
  filteredPresets: WorkPreset[];
  selectedPreset: WorkPreset | null;
  
  // 액션
  loadPresets: () => Promise<void>;
  setSearchQuery: (query: string) => void;
  filterPresets: () => void;
  selectPreset: (preset: WorkPreset) => void;
  clearSelection: () => void;
  
  // 프리셋 관리
  createPreset: (presetData: Omit<WorkPreset, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<void>;
  updatePreset: (id: string, updates: Partial<WorkPreset>) => Promise<void>;
  deletePreset: (id: string) => Promise<void>;
  duplicatePreset: (id: string, name: string) => Promise<void>;
  
  // 상호작용
  toggleFavorite: (id: string) => Promise<void>;
  ratePreset: (id: string, rating: number) => Promise<void>;
}

// 샘플 프리셋 데이터
const samplePresets: WorkPreset[] = [
  {
    id: '01JGHPQK2D8FYBMZE3AWQJRSPV',
    name: '번역 표준 프리셋',
    description: '일반적인 번역 작업을 위한 표준 설정',
    tags: ['표준', '기본'],
    category: 'translation',
    userInteraction: {
      isFavorite: false,
      usageCount: 15,
      rating: 4.5
    },
    metadata: {
      statistics: {
        totalUsages: 15,
        successRate: 95,
        averageCompletionTime: 2.5,
        averageCost: 150000
      },
      performance: {
        estimatedDuration: { value: 2, unit: 'day' },
        estimatedCost: 150000,
        complexity: 'medium'
      },
      requirements: {
        supportedLanguages: ['ko', 'en'],
        requiredSkills: ['translation', 'review']
      }
    },
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20'),
    createdBy: 'admin',
    configData: {
      selectedPresetId: '01JGHPQK2D8FYBMZE3AWQJRSPV',
      workflowComponent: {
        taskNodes: [
          { id: '01JGHPQM3X9KTFRHBN4MWQXS2P', type: 'transcription', order: 0, duration: { value: 1, unit: 'day' } },
          { id: '01JGHPQN5K7VGZBM8CTQWRXP4S', type: 'translation', order: 1, duration: { value: 1, unit: 'day' } },
          { id: '01JGHPQP2H6FJKRM9DTNXSYQ5V', type: 'translation_review', order: 2, duration: { value: 12, unit: 'hour' } }
        ],
        connections: []
      },
      workerAssignmentComponent: {
        assignmentMatrix: [],
        availableWorkers: [],
        availableGroups: []
      },
      finalConfirmationComponent: {
        jobs: [],
        expandedJobs: [],
        selectedLanguages: ['ko', 'en']
      }
    }
  },
  {
    id: '01JGHPQR7M8NJKWQ4HVTXSYP6Z',
    name: '고품질 번역 프리셋',
    description: '의료, 법률 등 전문 분야 번역용 고품질 설정',
    tags: ['고품질', '전문', '의료'],
    category: 'translation',
    userInteraction: {
      isFavorite: true,
      usageCount: 8,
      rating: 4.8
    },
    metadata: {
      statistics: {
        totalUsages: 8,
        successRate: 98,
        averageCompletionTime: 4.2,
        averageCost: 280000
      },
      performance: {
        estimatedDuration: { value: 4, unit: 'day' },
        estimatedCost: 280000,
        complexity: 'high'
      },
      requirements: {
        supportedLanguages: ['ko', 'en'],
        requiredSkills: ['medical_translation', 'legal_translation', 'review']
      }
    },
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-18'),
    createdBy: 'admin',
    configData: {
      selectedPresetId: '01JGHPQR7M8NJKWQ4HVTXSYP6Z',
      workflowComponent: {
        taskNodes: [
          { id: '01JGHPQS9P3QKFRM2NVXWRYQ7A', type: 'transcription', order: 0, duration: { value: 1, unit: 'day' } },
          { id: '01JGHPQT5L6HJKRM8DTNXSYP4B', type: 'translation', order: 1, duration: { value: 2, unit: 'day' } },
          { id: '01JGHPQV2H9MKFRN4QVTXSYQ5C', type: 'translation_review', order: 2, duration: { value: 1, unit: 'day' } },
          { id: '01JGHPQW8K7NJKRM9DTNXSYP6D', type: 'pm_check', order: 3, duration: { value: 4, unit: 'hour' } }
        ],
        connections: []
      },
      workerAssignmentComponent: {
        assignmentMatrix: [],
        availableWorkers: [],
        availableGroups: []
      },
      finalConfirmationComponent: {
        jobs: [],
        expandedJobs: [],
        selectedLanguages: ['ko', 'en']
      }
    }
  },
  {
    id: '01JGHPQX4N2PKFRQ6SVTXSYQ7E',
    name: '일본어 번역 프리셋',
    description: '한일 번역 전용 설정',
    tags: ['아시아', '문화'],
    category: 'translation',
    userInteraction: {
      isFavorite: false,
      usageCount: 12,
      rating: 4.3
    },
    metadata: {
      statistics: {
        totalUsages: 12,
        successRate: 92,
        averageCompletionTime: 2.8,
        averageCost: 180000
      },
      performance: {
        estimatedDuration: { value: 3, unit: 'day' },
        estimatedCost: 180000,
        complexity: 'medium'
      },
      requirements: {
        supportedLanguages: ['ko', 'ja'],
        requiredSkills: ['japanese_translation', 'cultural_adaptation']
      }
    },
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-22'),
    createdBy: 'admin',
    configData: {
      selectedPresetId: '01JGHPQX4N2PKFRQ6SVTXSYQ7E',
      workflowComponent: {
        taskNodes: [
          { id: '01JGHPQY6P5RJKFR8UVTXSYP9F', type: 'transcription', order: 0, duration: { value: 1, unit: 'day' } },
          { id: '01JGHPQZ2M8QKFRN4WVTXSYQ1G', type: 'translation', order: 1, duration: { value: 2, unit: 'day' } },
          { id: '01JGHPR19K7NJKRM9XVTXSYQ2H', type: 'translation_review', order: 2, duration: { value: 12, unit: 'hour' } }
        ],
        connections: []
      },
      workerAssignmentComponent: {
        assignmentMatrix: [],
        availableWorkers: [],
        availableGroups: []
      },
      finalConfirmationComponent: {
        jobs: [],
        expandedJobs: [],
        selectedLanguages: ['ko', 'ja']
      }
    }
  },
  {
    id: '01JGHPR25L8PKFRQ4YVTXSYQ3J',
    name: '전사 전용 프리셋',
    description: '번역 없이 전사 작업만 진행',
    tags: ['단순', '빠름'],
    category: 'transcription',
    userInteraction: {
      isFavorite: false,
      usageCount: 25,
      rating: 4.2
    },
    metadata: {
      statistics: {
        totalUsages: 25,
        successRate: 96,
        averageCompletionTime: 1.5,
        averageCost: 80000
      },
      performance: {
        estimatedDuration: { value: 1, unit: 'day' },
        estimatedCost: 80000,
        complexity: 'low'
      },
      requirements: {
        supportedLanguages: ['ko'],
        requiredSkills: ['transcription', 'review']
      }
    },
    createdAt: new Date('2024-01-08'),
    updatedAt: new Date('2024-01-19'),
    createdBy: 'admin',
    configData: {
      selectedPresetId: '01JGHPR25L8PKFRQ4YVTXSYQ3J',
      workflowComponent: {
        taskNodes: [
          { id: '01JGHPR37M9QKFRN6ZVTXSYQ4K', type: 'transcription', order: 0, duration: { value: 1, unit: 'day' } },
          { id: '01JGHPR49P2RJKFR8AVTXSYQ5L', type: 'transcription_review', order: 1, duration: { value: 12, unit: 'hour' } }
        ],
        connections: []
      },
      workerAssignmentComponent: {
        assignmentMatrix: [],
        availableWorkers: [],
        availableGroups: []
      },
      finalConfirmationComponent: {
        jobs: [],
        expandedJobs: [],
        selectedLanguages: ['ko']
      }
    }
  },
  {
    id: '01JGHPR55K6NJKFR2BVTXSYQ6M',
    name: '긴급 번역 프리셋',
    description: '24시간 내 완료 긴급 번역',
    tags: ['긴급', '빠른처리'],
    category: 'urgent',
    userInteraction: {
      isFavorite: true,
      usageCount: 5,
      rating: 4.6
    },
    metadata: {
      statistics: {
        totalUsages: 5,
        successRate: 90,
        averageCompletionTime: 0.8,
        averageCost: 200000
      },
      performance: {
        estimatedDuration: { value: 18, unit: 'hour' },
        estimatedCost: 200000,
        complexity: 'medium'
      },
      requirements: {
        supportedLanguages: ['ko', 'en'],
        requiredSkills: ['urgent_translation', 'quick_review']
      }
    },
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-25'),
    createdBy: 'admin',
    configData: {
      selectedPresetId: '01JGHPR55K6NJKFR2BVTXSYQ6M',
      workflowComponent: {
        taskNodes: [
          { id: '01JGHPR67M8PKFRN4CVTXSYQ7N', type: 'translation', order: 0, duration: { value: 8, unit: 'hour' } },
          { id: '01JGHPR79P1QJKFR6DVTXSYQ8P', type: 'translation_review', order: 1, duration: { value: 4, unit: 'hour' } }
        ],
        connections: []
      },
      workerAssignmentComponent: {
        assignmentMatrix: [],
        availableWorkers: [],
        availableGroups: []
      },
      finalConfirmationComponent: {
        jobs: [],
        expandedJobs: [],
        selectedLanguages: ['ko', 'en']
      }
    }
  }
];

export const useWorkPresetStore = create<WorkPresetStore>()(
  devtools(
    (set, get) => ({
      // 초기 상태
      presets: [],
      isLoading: false,
      searchQuery: '',
      filteredPresets: [],
      selectedPreset: null,

      // 프리셋 로드
      loadPresets: async () => {
        const { presets } = get();
        
        // 이미 프리셋이 있다면 다시 로드하지 않음 (새로 생성된 프리셋 보존)
        if (presets.length > 0) {
          return;
        }
        
        set({ isLoading: true });
        
        try {
          // 실제로는 API 호출
          await new Promise(resolve => setTimeout(resolve, 500));
          
          set({ 
            presets: samplePresets,
            filteredPresets: samplePresets,
            isLoading: false 
          });
        } catch (error) {
          console.error('프리셋 로드 실패:', error);
          set({ isLoading: false });
        }
      },

      // 검색어 설정
      setSearchQuery: (query) => {
        set({ searchQuery: query });
        get().filterPresets();
      },

      // 프리셋 필터링
      filterPresets: () => {
        const { presets, searchQuery } = get();
        
        if (!searchQuery.trim()) {
          set({ filteredPresets: presets });
          return;
        }

        const filtered = presets.filter(preset => {
          const searchTerm = searchQuery.toLowerCase();
          return (
            preset.name.toLowerCase().includes(searchTerm) ||
            preset.description.toLowerCase().includes(searchTerm) ||
            preset.tags.some(tag => tag.toLowerCase().includes(searchTerm))
          );
        });

        set({ filteredPresets: filtered });
      },

      // 프리셋 선택
      selectPreset: (preset) => {
        set({ selectedPreset: preset });
      },

      // 선택 해제
      clearSelection: () => {
        set({ 
          selectedPreset: null,
          searchQuery: '',
          filteredPresets: get().presets 
        });
      },
      
      // 프리셋 관리
      createPreset: async (presetData) => {
        const { presets } = get();
        const newPreset: WorkPreset = {
          ...presetData,
          id: ulid(),
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'current_user',
          userInteraction: {
            isFavorite: false,
            usageCount: 0,
            rating: null,
            lastUsedAt: null
          }
        };
        
        const updatedPresets = [...presets, newPreset];
        set({ presets: updatedPresets, filteredPresets: updatedPresets });
        get().filterPresets();
      },
      
      updatePreset: async (id, updates) => {
        const { presets } = get();
        const updatedPresets = presets.map(preset =>
          preset.id === id 
            ? { ...preset, ...updates, updatedAt: new Date() }
            : preset
        );
        set({ presets: updatedPresets, filteredPresets: updatedPresets });
        get().filterPresets();
      },
      
      deletePreset: async (id) => {
        const { presets } = get();
        const updatedPresets = presets.filter(preset => preset.id !== id);
        set({ presets: updatedPresets, filteredPresets: updatedPresets });
        get().filterPresets();
      },
      
      duplicatePreset: async (id, name) => {
        const { presets } = get();
        const originalPreset = presets.find(preset => preset.id === id);
        if (!originalPreset) return;
        
        const duplicatedPreset: WorkPreset = {
          ...originalPreset,
          id: ulid(),
          name,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'current_user',
          userInteraction: {
            isFavorite: false,
            usageCount: 0,
            rating: 0
          }
        };
        
        const updatedPresets = [...presets, duplicatedPreset];
        set({ presets: updatedPresets, filteredPresets: updatedPresets });
        get().filterPresets();
      },
      
      // 상호작용
      toggleFavorite: async (id) => {
        const { presets } = get();
        const updatedPresets = presets.map(preset =>
          preset.id === id 
            ? { 
                ...preset, 
                userInteraction: {
                  ...preset.userInteraction!,
                  isFavorite: !preset.userInteraction?.isFavorite
                }
              }
            : preset
        );
        set({ presets: updatedPresets, filteredPresets: updatedPresets });
        get().filterPresets();
      },
      
      ratePreset: async (id, rating) => {
        const { presets } = get();
        const updatedPresets = presets.map(preset =>
          preset.id === id 
            ? { 
                ...preset, 
                userInteraction: {
                  ...preset.userInteraction!,
                  rating
                }
              }
            : preset
        );
        set({ presets: updatedPresets, filteredPresets: updatedPresets });
        get().filterPresets();
      }
    }),
    { name: 'WorkPresetStore' }
  )
);
